import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Offsets for the history entries visited in THIS page session, keyed by
 * `${location.key}:${name}`.
 *
 * Deliberately in memory rather than sessionStorage. sessionStorage survives a
 * reload, and react-router gives every freshly-loaded document the same
 * location key — `default` — so a stored offset would be replayed onto an
 * unrelated later visit to the same route. A plain module-level Map is empty on
 * every document load, which makes "the user pressed back" the only way an
 * entry can exist to restore from.
 *
 * That also removes any need to ask react-router what kind of navigation this
 * is: useNavigationType() reports the first render of a document as a POP, so
 * it cannot tell a back from a page load either.
 */
const offsets = new Map<string, number>();

export interface ScrollRestorationOptions {
  /**
   * Distinguishes two scrollers on the same route — the job list has one for
   * the desktop table and one for the phone card stack, and only ever shows
   * one, so sharing a key would let a desktop offset land on a phone layout.
   */
  name: string;
  /**
   * False until the content has its real height. Restoring before then writes
   * a scrollTop the element cannot hold and the browser silently clamps it to
   * zero, which is indistinguishable from not restoring at all.
   */
  ready: boolean;
  /**
   * `element` restores the returned ref's own scrollTop; `window` restores
   * document scroll. Not inferred from whether a ref was attached, because
   * "the element has not mounted yet" and "this caller scrolls the window"
   * look identical from in here, and guessing wrong restores the wrong thing
   * once and then never again.
   */
  target?: 'element' | 'window';
}

/**
 * Puts a list back where the user left it when they come back to it.
 *
 * react-router's own <ScrollRestoration> is not an option: it exists only on a
 * data router (createBrowserRouter) and App mounts a plain <BrowserRouter>.
 * The browser's native restoration is not an option either — rows arrive from
 * React Query after the route renders, so at the moment the browser would
 * restore, the page has no height to restore into. Hence `ready`.
 *
 * Keyed by location.key, which react-router keeps stable per history entry and
 * hands back unchanged on a POP. Entering the same route by a link is a new
 * entry with a new key, so it starts at the top the way it always has.
 */
export function useScrollRestoration({
  name,
  ready,
  target = 'element',
}: ScrollRestorationOptions): (node: HTMLElement | null) => void {
  const { key } = useLocation();
  const storeKey = `${key}:${name}`;

  const [node, setNode] = useState<HTMLElement | null>(null);
  const ref = useCallback((next: HTMLElement | null) => setNode(next), []);

  const restored = useRef(false);
  const frame = useRef<number | null>(null);

  const scroller = target === 'window' ? null : node;
  const waitingForNode = target === 'element' && node === null;

  useEffect(() => {
    if (waitingForNode) return;

    const read = () => (scroller ? scroller.scrollTop : window.scrollY);

    // Coalesced to one write per frame: a scroll event fires on nearly every
    // wheel tick.
    const save = () => {
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        offsets.set(storeKey, read());
      });
    };

    const listener: EventTarget = scroller ?? window;
    listener.addEventListener('scroll', save, { passive: true });
    return () => {
      listener.removeEventListener('scroll', save);
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    };
  }, [scroller, waitingForNode, storeKey]);

  useLayoutEffect(() => {
    if (restored.current || !ready || waitingForNode) return;
    restored.current = true;

    const offset = offsets.get(storeKey);
    if (offset === undefined || offset <= 0) return;

    if (scroller) scroller.scrollTop = offset;
    else window.scrollTo(0, offset);
  }, [ready, waitingForNode, scroller, storeKey]);

  return ref;
}
